import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";

import {
  deleteCustomWidget,
  getCustomWidgetsList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  CustomWidgetItem,
  updateCustomWidgetsBunch,
  handleClearFields,
} from "store/cms/customized/slice";

import { bonusesListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";

import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";

import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import { GuideButton } from "modals/guides/GuideButton";
import { useGuideModal } from "modals/guides/useGuideModal";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const CustomizedList: React.FC = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  const { openGuide } = useGuideModal();

  const [currentLimit, setLimit] = useState<number>(bonusesListLimits[0]);
  
  const permissions = usePermissionHelper();

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store["cms-customized"].customWidgetsList);

  const isListFetched = useAppSelector(
    (store) => store["cms-customized"].isListFetched,
  );

  const totalItems = useAppSelector((store) => store["cms-customized"].totalItems);

  const totalPages = useAppSelector((store) => store["cms-customized"].totalPages);

  const activePage = useAppSelector((store) => store["cms-customized"].activePage);

  const selectedItems = useAppSelector(
    (store) => store["cms-customized"].selectedItems,
  );

  const processingItemId = useAppSelector(
    (state) => state["cms-customized"].processingItemId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-customized',
    isListFetched
  });

  const [search, setSearch] = useState<string>("");

  const [geos, setGeos] = useState<{ value: string | number; label: string }[]>(
    [],
  );

  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);

  const [order, setOrder] = useState<{
    title: string;
    id: string;
    isActive: string;
  }>({
    title: "none",
    id: "none",
    isActive: "none",
  });

  useEffect(() => {
    dispatch(handlePaginate(1));
  }, []);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getCustomWidgetsList({
          limit: currentLimit,

          offset: activePage - 1,

          title: search,

          geo: geos.map((el) => el.value as string),

          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,

          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };

  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    try {
      await dispatch(
        updateCustomWidgetsBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    dispatch(handleClearFields());

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.customized}&entityId=${id}`,
    );

    dispatch(
      openModal({
        activeModal: ModalTypes.customized,
      }),
    );
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.customized}`);

    dispatch(
      openModal({
        activeModal: ModalTypes.customized,
      }),
    );
  };

  const handleDeleteItem = async (id: number) => {
    const resultTag = await dispatch(deleteCustomWidget(id ?? 0));

    if (resultTag?.payload?.status === "OK") {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];

    let next: "asc" | "desc" | "none";

    if (current === "none") {
      next = "asc";
    } else if (current === "asc") {
      next = "desc";
    } else {
      next = "none";
    }

    const newOrder = {
      [key]: next,

      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };

    // @ts-ignore
    setOrder(newOrder);

    dispatch(handlePaginate(1));
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };

  const formattedList = list;

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--wo-filter">
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (<ActionBtn
              txt={t("addCustomWidget")}
              handleClick={handleOpenCreateModal}
            />)}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box page-list-header--box-actions">
            <GuideButton onClick={() => openGuide("cms")} />

            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={15} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && list.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noCustomWidgetsFound")} />
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <>
            <div className="game-table-wrap">
              <Table
                data={
                  formattedList as Array<
                    CustomWidgetItem & {
                      id: number;
                      itemsCount: number;
                      createdAtFormatted: string;
                    }
                  >
                }
                columns={{
                  hiddenColumns: ["items", "createdAt"],
                }}
                order={order}
                idField="id"
                selectedItems={selectedItems}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onDelete={(id) => handleDeleteItem(id)}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                processingId={processingItemId}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            </div>

            <Pagination
              handleLimitClick={(e) => {
                setLimit(e);

                dispatch(handlePaginate(1));
              }}
              limitsArray={bonusesListLimits}
              activePage={activePage}
              limit={currentLimit}
              totalItems={totalItems}
              totalPages={totalPages}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePrevClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CustomizedList;
